import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';

// Admin GETs must always return fresh data. The backend sets Cache-Control
// based on volatility (cold = 1h, warm = 5m) for downstream AI consumers;
// without cache-busting here, the browser would serve Werner a stale artifact
// after he saves an edit.
const cacheBust = (url) => `${url}${url.includes('?') ? '&' : '?'}_=${Date.now()}`;
const NO_CACHE = { headers: { 'Cache-Control': 'no-cache' } };
const adminGet = (url, config = {}) => api.get(cacheBust(url), { ...NO_CACHE, ...config, headers: { ...NO_CACHE.headers, ...(config.headers || {}) } });

const SYNC_CONTEXT_SLUG = 'sync_context';
const COMPILER_INSTRUCTIONS_SLUG = 'compiler_instructions';

const DARK_BG = '#0a0a0f';
const TEXT_PRIMARY = '#f0f0f5';
const TEXT_MUTED = 'rgba(240,240,245,0.4)';
const TEXT_SUBTLE = 'rgba(240,240,245,0.25)';
const BORDER_SOFT = 'rgba(255,255,255,0.06)';
const BORDER_MED = 'rgba(255,255,255,0.10)';
const ACCENT = '#6B35B8';

function MonoLabel({ children, style }) {
  return (
    <span
      style={{
        fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
        fontSize: 9,
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
        color: TEXT_SUBTLE,
        ...style,
      }}
    >
      {children}
    </span>
  );
}

function AdminButton({ onClick, disabled, children, tone = 'default' }) {
  const color = tone === 'primary' ? ACCENT : tone === 'danger' ? '#EF4444' : TEXT_PRIMARY;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
        fontSize: 10,
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
        color: disabled ? TEXT_SUBTLE : color,
        background: 'transparent',
        border: `1px solid ${disabled ? BORDER_SOFT : BORDER_MED}`,
        padding: '6px 16px',
        borderRadius: 3,
        cursor: disabled ? 'default' : 'pointer',
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.borderColor = color + '66';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = disabled ? BORDER_SOFT : BORDER_MED;
      }}
    >
      {children}
    </button>
  );
}

export default function AdminAIContextDocument() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [doc, setDoc] = useState(null);
  const [compiled, setCompiled] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [tab, setTab] = useState('source');
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [liveState, setLiveState] = useState('');
  const [liveUpdatedAt, setLiveUpdatedAt] = useState('');

  const isSyncContext = slug === SYNC_CONTEXT_SLUG;
  const isCompilerInstructions = slug === COMPILER_INSTRUCTIONS_SLUG;

  const fetchAll = async () => {
    setLoading(true);
    try {
      if (isSyncContext) {
        const [notesRes, liveRes] = await Promise.all([
          adminGet('/admin/sync-context/notes'),
          adminGet('/admin/sync-context/live'),
        ]);
        setNotesDraft(notesRes.data.notes_for_ai || '');
        setLiveState(liveRes.data.content || '');
        setLiveUpdatedAt(liveRes.data.updated_at || '');
        const meta = await adminGet(`/admin/ai-context/${slug}`);
        setDoc(meta.data);
      } else {
        const res = await adminGet(`/admin/ai-context/${slug}`);
        setDoc(res.data);
        setDraft(res.data.content || '');
        try {
          const compRes = await adminGet(`/admin/compiled/${slug}`);
          setCompiled(compRes.data);
        } catch {
          setCompiled(null);
        }
      }
    } catch (err) {
      setNotice(`Load failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const flashNotice = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(''), 5000);
  };

  const handleSave = async () => {
    setBusy('save');
    try {
      await api.put(`/admin/ai-context/${slug}`, { content: draft });
      flashNotice(isCompilerInstructions ? 'Saved. Instructions updated.' : 'Saved. Compiling in background...');
      setEditing(false);
      await fetchAll();
      if (!isCompilerInstructions) {
        setTimeout(async () => {
          try {
            const r = await adminGet(`/admin/compiled/${slug}`);
            setCompiled(r.data);
          } catch {
            /* still compiling */
          }
        }, 8000);
      }
    } catch (err) {
      flashNotice(`Save failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setBusy('');
    }
  };

  const handleRecompile = async () => {
    setBusy('recompile');
    try {
      await api.post(`/admin/ai-context/${slug}/recompile`);
      flashNotice('Recompiled');
      const r = await adminGet(`/admin/compiled/${slug}`);
      setCompiled(r.data);
    } catch (err) {
      flashNotice(`Recompile failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setBusy('');
    }
  };

  const handleResyncFromSource = async () => {
    if (!window.confirm(`Overwrite this document with the current repo source file? Any unsaved DB-only edits to ${slug} will be lost.`)) return;
    setBusy('resync');
    try {
      const res = await api.post(`/admin/ai-context/${slug}/resync-from-source`);
      flashNotice(`Resynced from ${res.data.source_file} (${res.data.size} bytes). Compiling in background.`);
      await fetchAll();
      setTimeout(async () => {
        try {
          const r = await adminGet(`/admin/compiled/${slug}`);
          setCompiled(r.data);
        } catch {
          /* still compiling */
        }
      }, 8000);
    } catch (err) {
      flashNotice(`Resync failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setBusy('');
    }
  };

  const handleSaveNotes = async () => {
    setBusy('notes');
    try {
      await api.put('/admin/sync-context/notes', { notes_for_ai: notesDraft });
      flashNotice('Notes for AI saved');
    } catch (err) {
      flashNotice(`Save failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setBusy('');
    }
  };

  const handleRegenerateLive = async () => {
    setBusy('regen');
    try {
      const res = await api.post('/admin/sync-context/regenerate');
      setLiveState(res.data.content || '');
      setLiveUpdatedAt(res.data.generated_at || '');
      flashNotice('Live state regenerated');
    } catch (err) {
      flashNotice(`Regenerate failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setBusy('');
    }
  };

  const compiledPretty = useMemo(() => {
    if (!compiled) return '';
    return JSON.stringify(compiled, null, 2);
  }, [compiled]);

  if (loading) {
    return (
      <div style={{ background: DARK_BG, minHeight: '100vh', padding: '48px 40px', color: TEXT_MUTED, fontFamily: "'DM Mono', 'JetBrains Mono', monospace", fontSize: 11 }}>
        Loading {slug}...
      </div>
    );
  }

  if (!doc) {
    return (
      <div style={{ background: DARK_BG, minHeight: '100vh', padding: '48px 40px', color: TEXT_MUTED, fontFamily: "'DM Mono', 'JetBrains Mono', monospace", fontSize: 11 }}>
        Document not found: {slug}
      </div>
    );
  }

  return (
    <div style={{ background: DARK_BG, minHeight: '100vh', padding: '48px 40px 80px' }}>
      <div style={{ maxWidth: 1060, margin: '0 auto' }}>
        {/* Breadcrumb */}
        <div style={{ marginBottom: 12 }}>
          <MonoLabel>
            <span style={{ cursor: 'pointer' }} onClick={() => navigate('/admin/architecture')}>
              Admin / AI Context
            </span>
            {' / '}
            <span style={{ color: TEXT_PRIMARY }}>{doc.title}</span>
          </MonoLabel>
        </div>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8, gap: 16, flexWrap: 'wrap' }}>
          <h1
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 30,
              fontWeight: 400,
              color: TEXT_PRIMARY,
              margin: 0,
            }}
          >
            {doc.title}
          </h1>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {!isSyncContext && !editing && (
              <>
                <AdminButton onClick={handleResyncFromSource} disabled={busy === 'resync'}>
                  {busy === 'resync' ? 'Resyncing...' : 'Resync from repo'}
                </AdminButton>
                <AdminButton onClick={() => { setDraft(doc.content || ''); setEditing(true); }}>
                  Edit
                </AdminButton>
              </>
            )}
            {!isSyncContext && editing && (
              <>
                <AdminButton onClick={handleSave} disabled={busy === 'save'} tone="primary">
                  {busy === 'save' ? 'Saving...' : 'Save'}
                </AdminButton>
                <AdminButton onClick={() => { setEditing(false); setDraft(doc.content || ''); }}>
                  Cancel
                </AdminButton>
              </>
            )}
          </div>
        </div>

        <div style={{ marginBottom: 32, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <MonoLabel>volatility · {doc.volatility}</MonoLabel>
          <MonoLabel>updated · {doc.updated_at ? new Date(doc.updated_at).toLocaleString() : '—'}</MonoLabel>
          <MonoLabel>by · {doc.updated_by || '—'}</MonoLabel>
        </div>

        {notice && (
          <div
            style={{
              fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
              fontSize: 10,
              letterSpacing: '0.1em',
              color: ACCENT,
              background: `${ACCENT}12`,
              border: `1px solid ${ACCENT}33`,
              padding: '8px 12px',
              borderRadius: 3,
              marginBottom: 18,
            }}
          >
            {notice}
          </div>
        )}

        {/* Sync Context dual-section layout */}
        {isSyncContext ? (
          <>
            <SectionHeader title="Notes for AI" />
            <textarea
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              style={editorStyle(200)}
              placeholder="Freeform markdown. Persists across regenerations."
            />
            <div style={{ marginTop: 10, marginBottom: 32 }}>
              <AdminButton onClick={handleSaveNotes} disabled={busy === 'notes'} tone="primary">
                {busy === 'notes' ? 'Saving...' : 'Save Notes'}
              </AdminButton>
            </div>

            <SectionHeader
              title="Live State (read-only)"
              right={
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  {liveUpdatedAt && (
                    <MonoLabel>updated · {new Date(liveUpdatedAt).toLocaleString()}</MonoLabel>
                  )}
                  <AdminButton onClick={handleRegenerateLive} disabled={busy === 'regen'}>
                    {busy === 'regen' ? 'Regenerating...' : 'Regenerate now'}
                  </AdminButton>
                </div>
              }
            />
            <pre style={readOnlyStyle}>{liveState || '(empty — click Regenerate now)'}</pre>
          </>
        ) : (
          <>
            {/* Tabs */}
            <div style={{ display: 'flex', gap: 2, marginBottom: 18, borderBottom: `1px solid ${BORDER_SOFT}` }}>
              {[
                { key: 'source', label: 'Source' },
                { key: 'compiled', label: 'Compiled', disabled: isCompilerInstructions },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => !t.disabled && setTab(t.key)}
                  disabled={t.disabled}
                  style={{
                    fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
                    fontSize: 10,
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    color: t.disabled ? TEXT_SUBTLE : tab === t.key ? TEXT_PRIMARY : TEXT_MUTED,
                    background: 'transparent',
                    border: 'none',
                    borderBottom: tab === t.key ? `1px solid ${ACCENT}` : '1px solid transparent',
                    padding: '10px 18px',
                    cursor: t.disabled ? 'default' : 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {t.label}
                  {t.disabled && ' · not compiled'}
                </button>
              ))}
              <div style={{ flex: 1 }} />
              {tab === 'compiled' && !isCompilerInstructions && (
                <div style={{ padding: '6px 0' }}>
                  <AdminButton onClick={handleRecompile} disabled={busy === 'recompile'}>
                    {busy === 'recompile' ? 'Compiling...' : 'Recompile'}
                  </AdminButton>
                </div>
              )}
            </div>

            {tab === 'source' && (
              <>
                {editing ? (
                  <textarea value={draft} onChange={(e) => setDraft(e.target.value)} style={editorStyle(560)} />
                ) : (
                  <pre style={readOnlyStyle}>{doc.content || '(empty)'}</pre>
                )}
              </>
            )}

            {tab === 'compiled' && (
              <>
                {compiled ? (
                  <CompiledView artifact={compiled} raw={compiledPretty} />
                ) : (
                  <div style={{ ...readOnlyStyle, color: TEXT_MUTED }}>
                    No compiled artifact yet. Save the source or click Recompile.
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SectionHeader({ title, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, marginBottom: 10 }}>
      <MonoLabel style={{ color: 'rgba(240,240,245,0.35)' }}>{title}</MonoLabel>
      {right}
    </div>
  );
}

function CompiledView({ artifact, raw }) {
  const [mode, setMode] = useState('pretty');
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {['pretty', 'raw'].map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            style={{
              fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
              fontSize: 9,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: mode === m ? TEXT_PRIMARY : TEXT_MUTED,
              background: 'transparent',
              border: `1px solid ${mode === m ? BORDER_MED : BORDER_SOFT}`,
              padding: '4px 10px',
              borderRadius: 3,
              cursor: 'pointer',
            }}
          >
            {m}
          </button>
        ))}
      </div>
      {mode === 'raw' ? (
        <pre style={readOnlyStyle}>{raw}</pre>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <MetaRow label="source_slug" value={artifact.source_slug} />
          <MetaRow label="compiler_version" value={artifact.compiler_version} />
          <MetaRow label="compiled_at" value={artifact.compiled_at} />
          <MetaRow label="source_hash" value={artifact.source_hash} mono />
          <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
            {(artifact.sections || []).map((s) => (
              <div
                key={s.slug}
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: `1px solid ${BORDER_SOFT}`,
                  borderRadius: 4,
                  padding: 16,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <span
                    style={{
                      fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
                      fontSize: 11,
                      color: ACCENT,
                    }}
                  >
                    {s.slug}
                  </span>
                  <div style={{ flex: 1, height: '0.5px', background: BORDER_SOFT }} />
                </div>
                {s.description && (
                  <p style={{ fontSize: 13, color: TEXT_PRIMARY, opacity: 0.8, margin: '0 0 6px' }}>
                    {s.description}
                  </p>
                )}
                {s.triggers && s.triggers.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                    {s.triggers.map((t) => (
                      <span
                        key={t}
                        style={{
                          fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
                          fontSize: 9,
                          letterSpacing: '0.05em',
                          color: TEXT_MUTED,
                          border: `0.5px solid ${BORDER_MED}`,
                          padding: '2px 6px',
                          borderRadius: 2,
                        }}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                )}
                <pre
                  style={{
                    fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
                    fontSize: 11,
                    color: TEXT_PRIMARY,
                    opacity: 0.8,
                    whiteSpace: 'pre-wrap',
                    margin: 0,
                  }}
                >
                  {s.content}
                </pre>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MetaRow({ label, value, mono = false }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
      <span
        style={{
          fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
          fontSize: 9,
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          color: TEXT_SUBTLE,
          minWidth: 140,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: mono ? "'DM Mono', 'JetBrains Mono', monospace" : "'Inter', sans-serif",
          fontSize: 12,
          color: TEXT_PRIMARY,
          wordBreak: mono ? 'break-all' : 'normal',
          opacity: 0.85,
        }}
      >
        {value}
      </span>
    </div>
  );
}

const editorStyle = (minHeight) => ({
  width: '100%',
  minHeight,
  background: 'rgba(255,255,255,0.03)',
  border: `1px solid ${BORDER_MED}`,
  borderRadius: 4,
  color: TEXT_PRIMARY,
  padding: 18,
  fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
  fontSize: 12,
  lineHeight: 1.55,
  resize: 'vertical',
  outline: 'none',
});

const readOnlyStyle = {
  background: 'rgba(255,255,255,0.03)',
  border: `1px solid ${BORDER_SOFT}`,
  borderRadius: 4,
  color: TEXT_PRIMARY,
  padding: 18,
  fontFamily: "'DM Mono', 'JetBrains Mono', monospace",
  fontSize: 12,
  lineHeight: 1.55,
  whiteSpace: 'pre-wrap',
  maxHeight: 700,
  overflowY: 'auto',
  margin: 0,
};
